import { useQuery } from "@tanstack/react-query";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

export const useDailySummary = (filters = {}) => {
  // Get date from filters or default to today
  const targetDate =
    filters.inserted_at || new Date().toISOString().split("T")[0];

  const queryString = buildQueryString({
    "filter[inserted_at]": targetDate,
    include: "paymentMethod",
    // Apply additional filters if provided
    ...(filters.payment_method_id && {
      "filter[payment_method_id]": filters.payment_method_id,
    }),
    ...(filters.cashier_session_id && {
      "filter[cashier_session_id]": filters.cashier_session_id,
    }),
    ...(filters.location && {
      "filter[location]": filters.location,
    }),
  });

  const {
    data: dailySummary,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["daily-summary", targetDate, filters], // Include filters in queryKey
    queryFn: async () => {
      const response = await getRequest(`/transaction${queryString}`);
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch daily summary.");
      }

      // Process the data to group by payment method
      const transactions = response.data || [];
      const summaryMap = {};

      transactions.forEach((transaction) => {
        const paymentMethodName = transaction.payment_method?.name || "Unknown";

        if (!summaryMap[paymentMethodName]) {
          summaryMap[paymentMethodName] = {
            paymentMethod: paymentMethodName,
            count: 0,
            totalAmount: 0,
          };
        }

        summaryMap[paymentMethodName].count += 1;
        summaryMap[paymentMethodName].totalAmount += Number(
          transaction.grand_total || 0
        );
      });

      // Convert to array and sort by total amount descending
      const summary = Object.values(summaryMap).sort(
        (a, b) => b.totalAmount - a.totalAmount
      );

      // Calculate grand totals
      const grandTotal = summary.reduce(
        (sum, item) => sum + item.totalAmount,
        0
      );
      const totalTransactions = summary.reduce(
        (sum, item) => sum + item.count,
        0
      );

      return {
        summary,
        grandTotal,
        totalTransactions,
        date: targetDate, // Use targetDate instead of today
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Prevent refetch on window focus
  });

  return {
    dailySummary,
    isLoading,
    error,
    refetch,
  };
};
